import { useCallback, useState } from "react";
import { validateRequiredFields } from "../_src_shared/utils/functions/validateRequiredFields";
import { useAxiosHook } from "./useAxiosHook";

/**
 * useReggoraRoute
 *
 * Handles all Reggora appraisal-ordering endpoints on the order engine.
 * Follows the same patterns as useAppOrdersEngineRoute (same base URL, same
 * useAxiosHook primitives).
 *
 * Flow:
 *   1. loanLookup          – find deal by loan number
 *   2. checkExistingLoans   – see if Reggora loans/orders already exist
 *   3. createLoans          – create Reggora loans (one per property)
 *   4. fetchProducts        – list appraisal product options
 *   5. submitOrders         – kick off Dapr workflow via SSE
 *   6. (SSE progress is handled inside submitOrders)
 *   7. fetchOrders          – get orders + notifications + webhook history
 *
 * Post-order:
 *   - fetchOrderDetails     – full Reggora order object
 *   - fetchDealDocuments    – list docs available for eVault upload
 *   - uploadEvaultDocs      – upload selected docs to Reggora eVault
 *   - holdOrder             – place order on hold
 *   - fetchConversation     – get order conversation thread
 *   - sendConversationMsg   – send message to order conversation
 *   - fetchLoanDiff         – diff snapshot vs current deal state
 *   - simulateWebhook       – dev-only webhook simulator
 */
export const useReggoraRouteHook = () => {
  const {
    axiosPostIsLoading,
    axiosGetIsLoading,
    axiosSSEIsLoading,
    axiosSSEProgress,
    axiosError,
    axiosGetRequest,
    axiosPostRequest,
    axiosSSERequest,
  } = useAxiosHook();

  const loading = axiosGetIsLoading || axiosPostIsLoading || axiosSSEIsLoading;

  // ─── Products cache (fetch once per session) ───
  const [products, setProducts] = useState(null);

  // ────────────────────────────────────────────────
  // Step 1: Loan Lookup
  // GET /reggora/loan-lookup/{loanNumber}
  // ────────────────────────────────────────────────
  const loanLookup = useCallback(
    async ({ loanIdentifier, onSuccessFn, onCompleteFn, onFailFn }) => {
      validateRequiredFields({ loanIdentifier });

      await axiosGetRequest({
        name: "Reggora Loan Lookup",
        type: "orderEngine",
        path: `reggora/loan-lookup/${loanIdentifier}`,
        onSuccessFn,
        onCompleteFn,
        onFailFn,
      });
    },
    [axiosGetRequest],
  );

  // ────────────────────────────────────────────────
  // Step 2: Check Existing Reggora State
  // GET /reggora/existing-loans/{deal_id}
  // ────────────────────────────────────────────────
  const checkExistingLoans = useCallback(
    async ({ dealId, onSuccessFn, onCompleteFn, onFailFn }) => {
      validateRequiredFields({ dealId });

      await axiosGetRequest({
        name: "Check Existing Reggora Loans",
        type: "orderEngine",
        path: `reggora/existing-loans/${dealId}`,
        onSuccessFn,
        onCompleteFn,
        onFailFn,
      });
    },
    [axiosGetRequest],
  );

  // ────────────────────────────────────────────────
  // Step 3: Create Reggora Loans
  // POST /reggora/create-loans
  // ────────────────────────────────────────────────
  const createLoans = useCallback(
    async ({ dealId, userEmail, onSuccessFn, onCompleteFn, onFailFn }) => {
      validateRequiredFields({ dealId, userEmail });

      await axiosPostRequest({
        name: "Create Reggora Loans",
        domain: "orderEngine",
        path: "reggora/create-loans",
        payload: {
          deal_id: dealId,
          user_email: userEmail,
        },
        onSuccessFn,
        onCompleteFn,
        onFailFn,
      });
    },
    [axiosPostRequest],
  );

  // ────────────────────────────────────────────────
  // Step 4: Fetch Appraisal Products
  // GET /reggora/products
  // ────────────────────────────────────────────────
  const fetchProducts = useCallback(
    async ({ onSuccessFn, onCompleteFn, onFailFn } = {}) => {
      // Return cached products if available
      if (products) {
        if (onSuccessFn) onSuccessFn(products);
        if (onCompleteFn) onCompleteFn(products);
        return;
      }

      await axiosGetRequest({
        name: "Fetch Reggora Products",
        type: "orderEngine",
        path: "reggora/products",
        onSuccessFn: (data) => {
          setProducts(data);
          if (onSuccessFn) onSuccessFn(data);
        },
        onCompleteFn,
        onFailFn,
      });
    },
    [axiosGetRequest, products],
  );

  // ────────────────────────────────────────────────
  // Step 5 + 6: Submit Orders (SSE workflow)
  // POST /reggora/submit-orders → SSE progress
  // ────────────────────────────────────────────────
  const submitOrders = useCallback(
    async ({
      dealId,
      loans,
      selectedProducts,
      dueDate,
      priority = "Normal",
      allocationType = "automatically",
      userEmail,
      onProgressFn,
      onProcessCompleteFn,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    }) => {
      validateRequiredFields({ dealId, loans, selectedProducts });

      await axiosSSERequest({
        name: "Submit Reggora Orders",
        domain: "orderEngine",
        path: "reggora/submit-orders",
        payload: {
          deal_id: dealId,
          loans,
          products: selectedProducts,
          due_date: dueDate,
          priority,
          allocation_type: allocationType,
          user_email: userEmail,
        },

        // SSE URL comes from the POST response
        getSSEUrl: (res) => res.sse_url,

        onProgressFn,
        onProcessCompleteFn,
        onSuccessFn: (resultData, initialPostResponse) => {
          if (onSuccessFn) onSuccessFn(resultData, initialPostResponse);
        },
        onCompleteFn,
        onFailFn,
      });
    },
    [axiosSSERequest],
  );

  // ────────────────────────────────────────────────
  // Step 7: Fetch Orders + Notifications
  // GET /reggora/orders/{deal_id}
  // ────────────────────────────────────────────────
  const fetchOrders = useCallback(
    async ({ dealId, onSuccessFn, onCompleteFn, onFailFn }) => {
      validateRequiredFields({ dealId });

      await axiosGetRequest({
        name: "Fetch Reggora Orders",
        type: "orderEngine",
        path: `reggora/orders/${dealId}`,
        onSuccessFn,
        onCompleteFn,
        onFailFn,
      });
    },
    [axiosGetRequest],
  );

  // ────────────────────────────────────────────────
  // Post-Order: Full Order Details from Reggora API
  // GET /reggora/order/{reggora_order_id}
  // ────────────────────────────────────────────────
  const fetchOrderDetails = useCallback(
    async ({ reggoraOrderId, onSuccessFn, onCompleteFn, onFailFn }) => {
      validateRequiredFields({ reggoraOrderId });

      await axiosGetRequest({
        name: "Fetch Reggora Order Details",
        type: "orderEngine",
        path: `reggora/order/${reggoraOrderId}`,
        onSuccessFn,
        onCompleteFn,
        onFailFn,
      });
    },
    [axiosGetRequest],
  );

  // ────────────────────────────────────────────────
  // Post-Order: Deal Documents for eVault Upload
  // GET /reggora/deal-documents/{deal_id}
  // ────────────────────────────────────────────────
  const fetchDealDocuments = useCallback(
    async ({ dealId, onSuccessFn, onCompleteFn, onFailFn }) => {
      validateRequiredFields({ dealId });

      await axiosGetRequest({
        name: "Fetch Deal Documents for eVault",
        type: "orderEngine",
        path: `reggora/deal-documents/${dealId}`,
        onSuccessFn,
        onCompleteFn,
        onFailFn,
      });
    },
    [axiosGetRequest],
  );

  // ────────────────────────────────────────────────
  // Post-Order: Upload Docs to Reggora eVault
  // POST /reggora/upload-evault-docs
  // ────────────────────────────────────────────────
  const uploadEvaultDocs = useCallback(
    async ({
      reggoraOrderId,
      dealId,
      docKeys,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    }) => {
      validateRequiredFields({ reggoraOrderId, dealId, docKeys });

      await axiosPostRequest({
        name: "Upload eVault Documents",
        domain: "orderEngine",
        path: "reggora/upload-evault-docs",
        payload: {
          reggora_order_id: reggoraOrderId,
          deal_id: dealId,
          doc_keys: docKeys,
        },
        onSuccessFn,
        onCompleteFn,
        onFailFn,
      });
    },
    [axiosPostRequest],
  );

  // ────────────────────────────────────────────────
  // Post-Order: Hold Order
  // PUT /reggora/hold-order
  // (useAxiosHook only has GET/POST — use POST with the
  //  understanding that the backend accepts PUT semantics
  //  or add a PUT method to useAxiosHook later)
  // ────────────────────────────────────────────────
  const holdOrder = useCallback(
    async ({ reggoraOrderId, reason, onSuccessFn, onCompleteFn, onFailFn }) => {
      validateRequiredFields({ reggoraOrderId, reason });

      // NOTE: The backend expects PUT. Since useAxiosHook doesn't expose
      // axiosPutRequest yet, we use axiosPostRequest. Two options:
      //   A) Add axiosPutRequest to useAxiosHook (recommended long-term)
      //   B) Use axiosPostRequest and have the backend also accept POST
      // Using POST for now — swap to PUT when the hook supports it.
      await axiosPostRequest({
        name: "Hold Reggora Order",
        domain: "orderEngine",
        path: "reggora/hold-order",
        payload: {
          reggora_order_id: reggoraOrderId,
          reason,
        },
        onSuccessFn,
        onCompleteFn,
        onFailFn,
      });
    },
    [axiosPostRequest],
  );

  // ────────────────────────────────────────────────
  // Post-Order: Fetch Conversation
  // GET /reggora/conversation/{order_id}
  // ────────────────────────────────────────────────
  const fetchConversation = useCallback(
    async ({ orderId, onSuccessFn, onCompleteFn, onFailFn }) => {
      validateRequiredFields({ orderId });

      await axiosGetRequest({
        name: "Fetch Reggora Conversation",
        type: "orderEngine",
        path: `reggora/conversation/${orderId}`,
        onSuccessFn,
        onCompleteFn,
        onFailFn,
      });
    },
    [axiosGetRequest],
  );

  // ────────────────────────────────────────────────
  // Post-Order: Send Conversation Message
  // POST /reggora/conversation/{order_id}/send
  // ────────────────────────────────────────────────
  const sendConversationMessage = useCallback(
    async ({ orderId, message, onSuccessFn, onCompleteFn, onFailFn }) => {
      validateRequiredFields({ orderId, message });

      await axiosPostRequest({
        name: "Send Reggora Conversation Message",
        domain: "orderEngine",
        path: `reggora/conversation/${orderId}/send`,
        payload: { message },
        onSuccessFn,
        onCompleteFn,
        onFailFn,
      });
    },
    [axiosPostRequest],
  );

  // ────────────────────────────────────────────────
  // Post-Order: Loan Diff (Change Detection)
  // GET /reggora/loan-diff/{deal_id}
  // ────────────────────────────────────────────────
  const fetchLoanDiff = useCallback(
    async ({ dealId, onSuccessFn, onCompleteFn, onFailFn }) => {
      validateRequiredFields({ dealId });

      await axiosGetRequest({
        name: "Fetch Reggora Loan Diff",
        type: "orderEngine",
        path: `reggora/loan-diff/${dealId}`,
        onSuccessFn,
        onCompleteFn,
        onFailFn,
      });
    },
    [axiosGetRequest],
  );

  // ────────────────────────────────────────────────
  // Dev-Only: Simulate Webhook
  // POST /reggora/test/simulate-webhook
  // ────────────────────────────────────────────────
  const simulateWebhook = useCallback(
    async ({
      reggoraOrderId,
      eventType,
      status,
      eventData,
      onSuccessFn,
      onCompleteFn,
      onFailFn,
    }) => {
      validateRequiredFields({ reggoraOrderId, eventType });

      await axiosPostRequest({
        name: "Simulate Reggora Webhook",
        domain: "orderEngine",
        path: "reggora/test/simulate-webhook",
        payload: {
          reggora_order_id: reggoraOrderId,
          event_type: eventType,
          status,
          event_data: eventData || {},
        },
        onSuccessFn,
        onCompleteFn,
        onFailFn,
      });
    },
    [axiosPostRequest],
  );

  return {
    loading,
    error: axiosError,
    sseProgress: axiosSSEProgress,
    products,

    // Steps 1-7
    loanLookup,
    checkExistingLoans,
    createLoans,
    fetchProducts,
    submitOrders,
    fetchOrders,

    // Post-order actions
    fetchOrderDetails,
    fetchDealDocuments,
    uploadEvaultDocs,
    holdOrder,
    fetchConversation,
    sendConversationMessage,
    fetchLoanDiff,
    simulateWebhook,
  };
};
